import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType) => {
    const id = Date.now().toString();
    setToasts((prevToasts) => [...prevToasts, { id, message, type }]);

    // Auto-remove toast after 5 seconds
    setTimeout(() => {
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, 5000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  const getToastClasses = (type: ToastType) => {
    const baseClasses = 'flex items-center justify-between p-4 mb-3 rounded-md shadow-md';

    switch (type) {
      case 'success':
        return `${baseClasses} bg-slate-100 text-slate-800 dark:bg-surface-800 dark:text-slate-100`;
      case 'error':
        return `${baseClasses} bg-slate-100 text-slate-800 dark:bg-surface-800 dark:text-slate-100`;
      case 'warning':
        return `${baseClasses} bg-slate-100 text-slate-800 dark:bg-surface-800 dark:text-slate-100`;
      case 'info':
      default:
        return `${baseClasses} bg-slate-100 text-slate-800 dark:bg-surface-800 dark:text-slate-100`;
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-50 w-80">
        {toasts.map((toast) => (
          <div key={toast.id} className={getToastClasses(toast.type)}>
            <div className="mr-2">{toast.message}</div>
            <button
              onClick={() => removeToast(toast.id)}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
